import React, { useState } from 'react';
import { ShieldAlert, Church, Loader2, CheckCircle } from 'lucide-react';
import { API_BASE } from '../utils/apiBase';

/**
 * Public account-deletion request page (no auth) — Google Play requirement.
 * Reachable at /supprimer-mon-compte without the app installed.
 */
const PublicDeleteRequest = () => {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Veuillez indiquer votre email.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/public/request-deletion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), reason: reason.trim() || undefined }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.detail || 'Une erreur est survenue.');
      }
      setSent(true);
    } catch (err) {
      setError(err?.message || 'Une erreur est survenue. Réessayez.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#00A99D]/10 flex items-center justify-center">
            <Church size={22} className="text-[#00A99D]" />
          </div>
          <span className="font-bold text-gray-900">Paris Centre Chrétien</span>
        </div>

        {sent ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center">
            <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
            <h1 className="text-lg font-bold text-gray-900 mb-2">Demande reçue</h1>
            <p className="text-sm text-gray-600">
              Votre demande a été reçue. Un membre de notre équipe la traitera sous 48h.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-4">
              <ShieldAlert size={20} className="text-red-600" />
              <h1 className="text-xl font-bold text-gray-900">Supprimer mon compte</h1>
            </div>

            <div className="rounded-xl bg-[#9398DE]/5 border border-[#9398DE]/20 p-3 mb-4">
              <p className="text-sm text-gray-700">
                Cette suppression concerne <strong>uniquement votre compte dans l'application</strong>.
                Elle ne vous retire pas de l'église et n'affecte pas votre suivi pastoral.
              </p>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Indiquez l'email associé à votre compte. Votre compte sera désactivé, puis vos données
              personnelles définitivement supprimées après un délai de 30 jours.
            </p>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-500">Email *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900"
                  placeholder="votre@email.com"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-500">Motif (facultatif)</label>
                <textarea
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900"
                  placeholder="Votre message (optionnel)"
                />
              </div>
              {error && <p className="text-sm text-red-500 text-center">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-60"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
                Envoyer ma demande
              </button>
            </form>
          </div>
        )}

        <p className="text-xs text-gray-400 text-center mt-4">
          Une question ? Écrivez à dev@pariscentrechretien.com
        </p>
      </div>
    </div>
  );
};

export default PublicDeleteRequest;
